import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { ParkingPenaltyRateDto, ParkingPenaltyRateMaster } from '../../../../core/models/system-settings/parking-master-data.model';
import { MapperParkingMasterDataService } from '../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { ApiParkingMasterDataService } from '../../../../core/services/api/system-settings/api-parking-master-data.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalParkingPenaltyRateComponent } from '../../modals/system-settings/modal-parking-penalty-rate/modal-parking-penalty-rate.component';

@Component({
  selector: 'app-parking-penalty-rate',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalParkingPenaltyRateComponent],
  templateUrl: './parking-penalty-rate.component.html',
  styleUrl: './parking-penalty-rate.component.css',
})
export class ParkingPenaltyRateComponent implements OnInit {
  private readonly api = inject(ApiParkingMasterDataService);
  private readonly mapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  items = signal<ParkingPenaltyRateMaster[]>([]);
  isLoading = signal(false);
  isModalOpen = signal(false);
  selectedId = signal(0);
  searchText = signal('');
  currentPage = signal(1);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(1);
  lastFetchedTime: Date | null = null;

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get startItemIndex(): number {
    return this.totalItems() === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    let s = Math.max(1, this.currentPage() - 2);
    let e = Math.min(this.totalPages(), s + 4);
    if (e - s < 4) s = Math.max(1, e - 4);
    return Array.from({ length: e - s + 1 }, (_, i) => s + i);
  }

  ngOnInit(): void {
    this.loadTable();
  }

  onSearchChange(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  openCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    this.selectedId.set(0);
    this.isModalOpen.set(true);
  }

  openEdit(item: ParkingPenaltyRateMaster): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.selectedId.set(item.id);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedId.set(0);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  trackById(_: number, item: ParkingPenaltyRateMaster): number {
    return item.id;
  }

  loadTable(): void {
    this.isLoading.set(true);
    this.api.getParkingPenaltyRateForTable({
      page: this.currentPage(),
      pageSize: this.pageSize(),
      searchText: this.searchText().trim() || undefined,
    }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapTable<ParkingPenaltyRateDto, ParkingPenaltyRateMaster>(
          res,
          (item) => this.mapper.mapPenaltyRate(item),
        );
        this.items.set(mapped.data);
        this.totalItems.set(mapped.total);
        this.totalPages.set(Math.max(1, Math.ceil(mapped.total / this.pageSize())));
        this.lastFetchedTime = new Date();
        this.isLoading.set(false);
      },
      error: () => {
        this.items.set([]);
        this.isLoading.set(false);
        this.toast.error('โหลดข้อมูลล้มเหลว');
      },
    });
  }
}
